import { useMemo, useRef } from 'react'
import { Group, Vector3, Mesh, Material } from 'three'
import { useFrame } from '@react-three/fiber'

// Empty in the GLTF that marks where the table light sits.
const LIGHT_NODE = 'tableLight'

// Cameras (0-indexed) where the table light is lit; it's off everywhere else.
const LIT_CAMS = new Set([7, 8])

// Point light look (no shadows).
const LIGHT_COLOR = '#ffcc99'
const LIGHT_INTENSITY = 0.3
const LIGHT_DISTANCE = 0
const LIGHT_DECAY = 3

// Portrait material that fades in. Hidden by default, fades to PORTRAIT_OPACITY
// while PORTRAIT_CAM is active.
const PORTRAIT_MATERIAL = 'x_fredro_FADEIN'
const PORTRAIT_CAM = 8
const PORTRAIT_OPACITY = 0.5
// Approximate time (s) the eased fade takes to settle. SmoothDamp gives an
// S-curve (eases in and out), so the fade-in grows smoothly from 0 instead of
// popping. Fade-out is kept short so leaving the camera clears quickly.
const PORTRAIT_FADE_IN_TIME = 2.0
const PORTRAIT_FADE_OUT_TIME = 0.4
const PORTRAIT_FADE_IN_DELAY = 0.5 // seconds on PORTRAIT_CAM before the fade-in starts

// Critically damped spring (Unity-style SmoothDamp). Unlike exponential damping
// it starts from the current velocity and decelerates into the target, so a fade
// from rest accelerates then settles — no instant jump on the first frame.
function smoothDamp(current: number, target: number, vel: { v: number }, smoothTime: number, delta: number): number {
  const omega = 2 / Math.max(0.0001, smoothTime)
  const x = omega * delta
  const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x)
  const change = current - target
  const temp = (vel.v + omega * change) * delta
  vel.v = (vel.v - omega * temp) * exp
  let output = target + (change + temp) * exp
  // Prevent overshooting past the target.
  if (target - current > 0 === output > target) {
    output = target
    vel.v = (output - target) / delta
  }
  return output
}

interface AnimatedLightAndPortraitProps {
  scene: Group
  activeCam: number
}

// A point light parented to the `tableLight` empty in the room (no shadows, only
// lit on LIT_CAMS), plus a portrait material that fades in while PORTRAIT_CAM is
// active.
export default function AnimatedLightAndPortrait({ scene, activeCam }: AnimatedLightAndPortraitProps) {
  const position = useMemo(() => {
    const node = scene.getObjectByName(LIGHT_NODE)
    if (!node) return new Vector3()
    node.updateWorldMatrix(true, false)
    return node.getWorldPosition(new Vector3())
  }, [scene])

  // Find the fade-in portrait material and prime it: transparent + alpha 0.
  const portraitMat = useMemo(() => {
    let found: Material | null = null
    scene.traverse((obj) => {
      const mat = (obj as Mesh).material as Material | Material[] | undefined
      if (!mat) return
      const list = Array.isArray(mat) ? mat : [mat]
      const hit = list.find((m) => m.name === PORTRAIT_MATERIAL)
      if (hit) found = hit
    })
    if (found) {
      const m = found as Material
      m.transparent = true
      m.opacity = 0
    }
    return found as Material | null
  }, [scene])

  // Time spent on PORTRAIT_CAM, used to hold the fade-in back briefly after arrival.
  const portraitCamDwell = useRef(0)
  // Fade velocity carried across frames by the spring.
  const portraitVel = useRef({ v: 0 })

  // Ease the portrait alpha toward its target for the active camera. The fade-in
  // waits PORTRAIT_FADE_IN_DELAY after landing on PORTRAIT_CAM, then springs
  // smoothly up; leaving fades out quickly.
  useFrame((_, delta) => {
    if (!portraitMat) return
    const onPortraitCam = activeCam === PORTRAIT_CAM
    portraitCamDwell.current = onPortraitCam ? portraitCamDwell.current + delta : 0
    const fadingIn = onPortraitCam && portraitCamDwell.current >= PORTRAIT_FADE_IN_DELAY
    const target = fadingIn ? PORTRAIT_OPACITY : 0
    const smoothTime = fadingIn ? PORTRAIT_FADE_IN_TIME : PORTRAIT_FADE_OUT_TIME
    portraitMat.opacity = smoothDamp(portraitMat.opacity, target, portraitVel.current, smoothTime, delta)
  })

  return (
    <pointLight
      position={position}
      color={LIGHT_COLOR}
      intensity={LIT_CAMS.has(activeCam) ? LIGHT_INTENSITY : 0}
      distance={LIGHT_DISTANCE}
      decay={LIGHT_DECAY}
    />
  )
}
